var _FordUrgencyBadge, _FordUrgencyBadge2, _FordUrgencyBadge3, _Text, _Badge;
import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';
import { useFeatures } from '@atc/features';
import { useBrand } from '@atc/brand';
import { Badge, Glyphicon, LazyRender, Text } from 'reaxl';
import { PricingV2, ListingPaymentCTA, PricingBadges } from 'reaxl-molecules';
import { WalletPaymentDetailsV2 } from 'reaxl-wallet';
import InventoryListingInfo from '../InventoryListingInfo/index.js';
import InventoryListingTitle from '../InventoryListingTitle/index.js';
import InventoryListingBadges from '../InventoryListingBadges/index.js';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const FordUrgencyBadge = /*#__PURE__*/React.memo(_ref => {
  let {
    label
  } = _ref;
  const color = label === 'Reduced Price' ? 'success' : 'accent';
  const dataCmp = label.replace(/\s+/g, '').charAt(0).toLowerCase() + label.slice(1);
  return /*#__PURE__*/_jsx(Text, {
    "data-cmp": dataCmp,
    weight: "bold",
    color: color,
    antialiased: true,
    children: label
  });
});
const UrgencyBadges = /*#__PURE__*/React.memo(_ref2 => {
  let {
    isNewlyListed,
    isPreorder,
    isReducedPrice,
    isHot,
    uiContext,
    isFord
  } = _ref2;
  let urgencyBadge = null;
  if (isHot && uiContext !== 'snapshot') {
    urgencyBadge = _FordUrgencyBadge || (_FordUrgencyBadge = /*#__PURE__*/_jsx(FordUrgencyBadge, {
      label: "High Demand"
    }));
  } else if (isNewlyListed) {
    urgencyBadge = _FordUrgencyBadge2 || (_FordUrgencyBadge2 = /*#__PURE__*/_jsx(FordUrgencyBadge, {
      label: "Newly Listed"
    }));
  } else if (isReducedPrice) {
    urgencyBadge = _FordUrgencyBadge3 || (_FordUrgencyBadge3 = /*#__PURE__*/_jsx(FordUrgencyBadge, {
      label: "Reduced Price"
    }));
  }
  if (!isFord && !isPreorder) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    className: "padding-bottom-1 margin-right-2",
    children: [isFord && urgencyBadge, isPreorder && (_Text || (_Text = /*#__PURE__*/_jsx(Text, {
      className: "margin-right-2",
      "data-cmp": "customBuild",
      antialiased: true,
      color: "accent",
      weight: "bold",
      children: "Custom Order"
    })))]
  }, "badges");
});
const PricingSection = /*#__PURE__*/React.memo(_ref3 => {
  let {
    isPreorder,
    priceLabel,
    priceDescription,
    onPricingDetailClick,
    orientation,
    pricingDetail,
    hasIncentive,
    showPayments,
    listing,
    renderPaymentDetails,
    showListingPaymentRibbon,
    enableMyWallet,
    onPaymentCTAClick,
    uiContext
  } = _ref3;
  const pricingContext = showPayments ? 'listing' : 'details';
  const pricingUiContext = uiContext === 'recommended' ? 'default' : pricingContext;
  const pricingComponent = /*#__PURE__*/_jsx(PricingV2, {
    data: pricingDetail,
    isPreorder: isPreorder,
    priceLabel: priceLabel,
    priceDescription: priceDescription,
    uiContext: pricingUiContext,
    onPricingDetailClick: onPricingDetailClick,
    orientation: orientation
  }, "inventory-listing-pricing");
  const isRecommended = uiContext === 'recommended';
  const isSnapshot = uiContext === 'snapshot';
  const paymentCTAComponent = /*#__PURE__*/_jsx(ListingPaymentCTA, {
    className: "text-size-200",
    ctaText: isRecommended ? 'See details' : 'See payment',
    onPaymentCTAClick: onPaymentCTAClick
  }, "paymentCTA");
  const showCTA = !isSnapshot && enableMyWallet && !listing.accelerate;
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs("div", {
      className: "pricing-payments",
      children: [!isRecommended && pricingComponent, !isSnapshot && showListingPaymentRibbon && renderPaymentDetails(), !isRecommended && !showListingPaymentRibbon && showCTA && paymentCTAComponent, isRecommended && pricingComponent, isRecommended && showCTA && paymentCTAComponent]
    }), /*#__PURE__*/_jsx(PricingBadges, {
      dealIndicator: pricingDetail.dealIndicator,
      hasIncentive: hasIncentive,
      uiContext: typeof uiContext === 'string' ? uiContext : undefined
    })]
  }, "pricing-section");
});
/**
 *  Describe the component
 *
 */
function InventoryListingBodyV2(_ref4) {
  let {
    enableMyWallet = false,
    isActiveDeal,
    isSaved,
    listing,
    listingCTA,
    priceLabel,
    priceDescription,
    onCertifiedTileClick,
    onContextMenu,
    onMyWalletClick,
    onPaymentCTAClick,
    onPricingDetailClick,
    onProductTileClick,
    onProductTileLinkClick,
    onVhrBadgeClick,
    onSaveToggle,
    orientation,
    paymentsCTA,
    panelHeaderText,
    personalPaymentProps = {},
    showListingPaymentRibbon = false,
    showPackages = true,
    showPaymentDetailsLink,
    uiContext,
    updateListingPayments,
    ...rest
  } = _ref4;
  const {
    enable_cpo_vhr_badges: [, {
      show_cpo_vhr_badges: enableCpoVhrBadges
    }],
    enable_vhr_badges: [enableVhrBadges],
    inventory_carousel: [enableInventoryCarousel]
  } = useFeatures(['enable_cpo_vhr_badges', 'enable_vhr_badges', 'inventory_carousel']);
  const {
    isBrand
  } = useBrand();
  const isFord = isBrand('ford');
  const {
    isNewlyListed,
    isReducedPrice,
    isLessMileage,
    isHot,
    pricingDetail = {},
    hasIncentive,
    title,
    listingType,
    certifiedTiles = [],
    year,
    make,
    model,
    trim,
    fuelType,
    specifications
  } = listing;
  const isSnapshot = uiContext === 'snapshot';
  // Don't display the inventory listing badges if the FF is on and the context is 'default'
  const isDefaultCtxWithFlag = uiContext === 'default' && enableInventoryCarousel;
  const isPreorder = uiContext === 'preorder';
  const isSimplifiedListing = uiContext === 'simplified';
  const renderPaymentDetails = className => !isActiveDeal && /*#__PURE__*/_jsx(LazyRender, {
    height: 22,
    children: /*#__PURE__*/_jsx("div", {
      className: clsx(className, 'display-flex'),
      children: /*#__PURE__*/_jsx(WalletPaymentDetailsV2, {
        listingId: listing.id,
        isPreorder: isPreorder,
        onMyWalletClick: onMyWalletClick,
        personalPaymentProps: personalPaymentProps,
        uiContext: uiContext === 'recommended' ? 'default' : 'ribbon',
        updateListingPayments: updateListingPayments
      })
    })
  }, "paymentDetails");
  const titleProps = {
    ...listingCTA,
    title: title || listingCTA?.title,
    size: 400,
    onContextMenu,
    uiContext
  };
  const listingInfoProps = {
    year,
    make: make?.name,
    model: model?.name,
    trim: trim?.name,
    mileage: specifications?.mileage?.value,
    engine: specifications?.engine?.value,
    fuelType,
    fuelGroup: fuelType?.group,
    listingType,
    certifiedTiles,
    onCertifiedTileClick
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "inventory-listing-body padding-top-3 padding-horizontal-3",
    ...rest,
    children: [uiContext === 'recommended' && /*#__PURE__*/_jsx("div", {
      className: "row",
      children: /*#__PURE__*/_jsx("div", {
        className: "col-xs-12 margin-bottom-1",
        children: /*#__PURE__*/_jsx(Text, {
          size: 400,
          color: "blue-darker",
          weight: "bold",
          children: panelHeaderText
        })
      })
    }), /*#__PURE__*/_jsxs("div", {
      children: [(isNewlyListed || isPreorder || isReducedPrice || isHot) && /*#__PURE__*/_jsx(UrgencyBadges, {
        isNewlyListed: isNewlyListed,
        isPreorder: isPreorder,
        isReducedPrice: isReducedPrice,
        isHot: isHot,
        uiContext: uiContext,
        isFord: isFord
      }), /*#__PURE__*/_jsx("div", {
        className: "display-flex justify-content-between",
        children: isPreorder ? /*#__PURE__*/_jsx(InventoryListingTitle, {
          ...titleProps
        }) : /*#__PURE__*/_jsx(InventoryListingInfo, {
          ...titleProps,
          ...listingInfoProps
        })
      }, "title"), /*#__PURE__*/_jsx(PricingSection, {
        isPreorder: isPreorder,
        listing: listing,
        priceLabel: priceLabel,
        priceDescription: priceDescription,
        pricingDetail: pricingDetail,
        hasIncentive: hasIncentive,
        onPricingDetailClick: onPricingDetailClick,
        orientation: orientation,
        showPayments: !isSnapshot,
        renderPaymentDetails: renderPaymentDetails,
        showListingPaymentRibbon: showListingPaymentRibbon,
        enableMyWallet: enableMyWallet,
        onPaymentCTAClick: onPaymentCTAClick,
        uiContext: uiContext
      }), !isSimplifiedListing && !isSnapshot && !isDefaultCtxWithFlag && /*#__PURE__*/_jsx(InventoryListingBadges, {
        enableCertifiedBadges: isPreorder,
        enableCpoVhrBadges: enableCpoVhrBadges,
        enableVhrBadges: enableVhrBadges,
        enableFuelTypeBadges: isPreorder,
        listing: listing,
        onCertifiedTileClick: onCertifiedTileClick,
        onProductTileClick: onProductTileClick,
        onVhrBadgeClick: onVhrBadgeClick
      })]
    }), isLessMileage && uiContext === 'snapshot' && (_Badge || (_Badge = /*#__PURE__*/_jsxs(Badge, {
      className: "display-inline-flex gap-1 align-items-baseline text-normal text-size-200 margin-top-3 warning",
      children: [/*#__PURE__*/_jsx(Glyphicon, {
        glyph: "mileage",
        className: "warning text-size-300"
      }), "Less Mileage"]
    })))]
  });
}
export default InventoryListingBodyV2;